import { ResponsiveTable } from "../mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useState } from "react";
import { authStore } from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { PhoneIncoming, PhoneOutgoing, Voicemail, PhoneCall, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const INTENT_CLS = {
  "高意向": "bg-emerald-100 text-emerald-800", "已约现场": "bg-emerald-100 text-emerald-800",
  "中意向": "bg-blue-100 text-blue-800", "价格敏感": "bg-amber-100 text-amber-800",
  "需回拨": "bg-amber-100 text-amber-800", "低意向": "bg-slate-100 text-slate-600",
  "无效来电": "bg-red-50 text-red-600", "其他": "bg-slate-100 text-slate-500",
};
export const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
export const fmtTime = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const STATUS_ZH = { completed: "已接通", "no-answer": "未接", busy: "忙线", failed: "失败", canceled: "已取消", ringing: "响铃", "in-progress": "进行中" };

export function RecordingPlayer({ id, testid }) {
  const token = authStore.get("ali_token") || "";
  return <audio data-testid={testid} controls preload="none" className="h-8 w-52 max-w-[220px]" src={`${BACKEND}/api/telephony/calls/${id}/recording?auth=${token}`} />;
}

export function Stars({ n, size = "h-3.5 w-3.5", testid }) {
  return <span className="inline-flex text-amber-500" data-testid={testid} data-stars={n}>{[1, 2, 3, 4, 5].map((i) => <span key={i} className={i <= n ? "" : "text-slate-200"}>★</span>)}</span>;
}

/* 坐席通话记录表 */
export function AgentCallsTable({ calls, zh }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const dial = (c) => window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number: c.customerNumber, name: c.name } }));
  if (!calls.length) return <p className="text-sm text-slate-400 text-center py-10" data-testid="agent-calls-empty">{zh ? "该时间范围内无通话记录" : "No calls in this range"}</p>;
  return (
    <div className="overflow-x-auto" data-testid="agent-calls-table" data-count={calls.length}>
      <ResponsiveTable className="w-full text-sm min-w-[720px]">
        <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
          <th className="py-2 px-3">{zh ? "时间" : "Time"}</th><th className="py-2 px-3">{zh ? "方向" : "Dir"}</th>
          <th className="py-2 px-3">{zh ? "客户 / 号码" : "Customer"}</th><th className="py-2 px-3">{zh ? "状态" : "Status"}</th>
          <th className="py-2 px-3 text-right">{zh ? "时长" : "Duration"}</th><th className="py-2 px-3">{zh ? "意向 / 评分" : "Intent / Score"}</th>
          <th className="py-2 px-3">{zh ? "录音" : "Recording"}</th><th className="py-2 px-3" />
        </tr></thead>
        <tbody className="divide-y divide-slate-50">
          {calls.map((c) => (
            <React.Fragment key={c.id}>
              <tr data-testid={`agent-call-${c.id}`} className="hover:bg-slate-50/60">
                <td className="py-2 px-3 text-xs text-slate-500 whitespace-nowrap">{fmtTime(c.createdAt)}</td>
                <td className="py-2 px-3">{c.isVoicemail ? <Voicemail className="h-4 w-4 text-purple-500" /> : c.direction === "inbound" ? <PhoneIncoming className="h-4 w-4 text-sky-600" /> : <PhoneOutgoing className="h-4 w-4 text-emerald-600" />}</td>
                <td className="py-2 px-3">
                  {c.leadId ? <button onClick={() => navigate(`/crm/leads/${c.leadId}`)} className="text-amber-700 hover:underline font-medium">{c.name || c.customerNumber}</button> : <span className="font-medium text-slate-800">{c.name || c.customerNumber || "—"}</span>}
                  <span className="block text-[11px] font-mono text-slate-400">{c.customerNumber}</span>
                </td>
                <td className="py-2 px-3"><span className={`text-[11px] rounded-full px-2 py-0.5 ${c.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{zh ? (STATUS_ZH[c.status] || c.status || "—") : (c.status || "—")}</span></td>
                <td className="py-2 px-3 text-right font-mono text-xs text-slate-600">{fmtDur(c.durationSec)}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {c.intentTag && <span className={`text-[11px] rounded-full px-2 py-0.5 ${INTENT_CLS[c.intentTag] || "bg-slate-100 text-slate-500"}`}>{c.intentTag}</span>}
                    {typeof c.score === "number" && <span className="text-[11px] font-bold text-amber-700">{c.score}</span>}
                    {c.managerRating?.stars && <Stars n={c.managerRating.stars} />}
                  </div>
                </td>
                <td className="py-2 px-3">{c.hasRecording ? <RecordingPlayer id={c.id} testid={`agent-call-rec-${c.id}`} /> : <span className="text-slate-300 text-xs">—</span>}</td>
                <td className="py-2 px-3 whitespace-nowrap">
                  <button data-testid={`agent-call-dial-${c.id}`} onClick={() => dial(c)} className="text-emerald-600 hover:text-emerald-800 mr-2" title={zh ? "回拨" : "Call back"}><PhoneCall className="h-4 w-4" /></button>
                  {(c.summary || c.transcript) && <button data-testid={`agent-call-toggle-${c.id}`} onClick={() => setOpen(open === c.id ? null : c.id)} className="text-slate-400 hover:text-slate-700">{open === c.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>}
                </td>
              </tr>
              {open === c.id && (
                <tr data-testid={`agent-call-detail-${c.id}`}><td colSpan={8} className="px-4 py-3 bg-slate-50/70 text-sm text-slate-700">
                  {c.summary && <p><b className="text-slate-500 text-xs">{zh ? "AI 摘要：" : "Summary: "}</b>{c.summary}</p>}
                  {c.highlight && <p className="mt-1 text-emerald-700 text-xs">✦ {c.highlight}</p>}
                  {c.transcript && <p className="mt-1 text-xs text-slate-500 whitespace-pre-wrap max-h-40 overflow-y-auto">{c.transcript}</p>}
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </ResponsiveTable>
    </div>
  );
}
